import { safeSet } from '../lib/storage.js';
import { createContext, useContext, useState, useEffect } from 'react';
import { translations, RTL_LANGS } from '../i18n.js';
import { THEMES, buildTokens, DEFAULT_THEME, BG_FX } from '../theme/themes.js';

const SettingsContext = createContext(null);

// THEMES (встроенные) живут в src/theme/themes.js. Ре-экспорт для совместимости.
export { THEMES };

export const FONTS = {
  mono:    { name:'Моноширинный', nameKey:'fnt_mono', stack:"'Courier New', monospace" },
  system:  { name:'Системный', nameKey:'fnt_system',    stack:"-apple-system, 'Segoe UI', Roboto, sans-serif" },
  manrope: { name:'Манроп', nameKey:'fnt_manrope',   stack:"'Manrope', -apple-system, sans-serif" },
  serif:   { name:'Серифный', nameKey:'fnt_serif',     stack:"'Cormorant Garamond', Georgia, 'Times New Roman', serif" },
  rounded: { name:'Округлый', nameKey:'fnt_rounded',     stack:"'Trebuchet MS', 'Segoe UI', sans-serif" },
  // unusual
  impact:  { name:'Жирный Impact', nameKey:'fnt_impact', stack:"Impact, 'Arial Black', sans-serif" },
  comic:   { name:'Комикс', nameKey:'fnt_comic',        stack:"'Comic Sans MS', 'Comic Sans', cursive" },
  cursive: { name:'Рукописный', nameKey:'fnt_cursive',    stack:"'Brush Script MT', 'Segoe Script', cursive" },
  wide:    { name:'Широкий', nameKey:'fnt_wide',       stack:"'Arial Black', 'Franklin Gothic', sans-serif" },
  typewriter:{ name:'Печатная машинка', nameKey:'fnt_typewriter', stack:"'Lucida Console', 'Courier New', monospace" },
};

const readJSON = (k, fb) => { try { const v = JSON.parse(localStorage.getItem(k) || ''); return v && typeof v === 'object' ? v : fb; } catch { return fb; } };

export function SettingsProvider({ children }) {
  const [theme, setThemeState]   = useState(() => localStorage.getItem('ui_theme') || DEFAULT_THEME);
  const [customColor, setCustomColor] = useState(() => localStorage.getItem('ui_custom_color') || '#0d0d10');
  // Optional manual overrides. '' = use the theme value.
  const [customText, setCustomText] = useState(() => localStorage.getItem('ui_custom_text') || '');
  const [customCard, setCustomCard] = useState(() => localStorage.getItem('ui_custom_card') || '');
  // Фон — отдельный слой ПОВЕРХ темы: анимация фона (BG_FX). Пустая строка/'theme'
  // = использовать то, что задаёт сама тема; иначе — переопределение пользователем.
  const [bgFx, setBgFxState] = useState(() => localStorage.getItem('ui_bg_fx') || 'theme');
  const [font, setFontState]     = useState(() => localStorage.getItem('ui_font') || 'manrope');
  const [fontScale, setScaleState] = useState(() => parseFloat(localStorage.getItem('ui_font_scale')) || 1);
  // По умолчанию английский, а не русский: по публичной ссылке приходят
  // англоязычные. Если браузер просит поддерживаемый язык — берём его.
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem('ui_lang');
      if (saved) return saved;
      const nav = (navigator.language || '').slice(0, 2).toLowerCase();
      return ['ru', 'en', 'es', 'ar', 'de'].includes(nav) ? nav : 'en';
    } catch { return 'en'; }
  });
  const [showCat, setShowCatState] = useState(() => localStorage.getItem('ui_show_cat') !== '0');
  // User-saved themes (full specs). Map id -> spec. Persisted as JSON.
  const [customThemes, setCustomThemes] = useState(() => readJSON('ui_custom_themes', {}));
  // Transient draft used by the Theme Studio for LIVE preview while editing.
  // Not persisted; cleared on save/cancel. When set, it overrides everything.
  const [themeDraft, setThemeDraftState] = useState(null);
  // Selected pet skin (groundwork for multiple animals).
  const [pet, setPetState] = useState(() => localStorage.getItem('ui_pet') || 'cat');

  const setTheme = (t) => { setThemeDraftState(null); setThemeState(t); safeSet('ui_theme', t);
    try { window.dispatchEvent(new CustomEvent('theme_switching', { detail: { theme: t } })); } catch {}   try {
    const tk = localStorage.getItem('auth_token');
    if (tk) fetch('/api/users/me', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ uiPrefs: { theme: t } }) }).catch(() => {});
  } catch { /* доедет со следующей сменой */ }
};
  const setCustom = (c) => { setCustomColor(c); safeSet('ui_custom_color', c); setTheme('custom'); };
  const setCustomTextColor = (c) => { setCustomText(c || ''); safeSet('ui_custom_text', c || ''); };
  const setCustomCardColor = (c) => { setCustomCard(c || ''); safeSet('ui_custom_card', c || ''); };
  const setFont = (f) => { setFontState(f); safeSet('ui_font', f); };
  const setFontScale = (s) => { setScaleState(s); safeSet('ui_font_scale', String(s)); };
  const setShowCat = (v) => { setShowCatState(v); safeSet('ui_show_cat', v ? '1' : '0'); };
  const setLang = (l) => { setLangState(l); safeSet('ui_lang', l);   try {
    const tk = localStorage.getItem('auth_token');
    if (tk) fetch('/api/users/me', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ uiPrefs: { lang: l } }) }).catch(() => {});
  } catch { /* язык доедет со следующей сменой */ }
};
  const setPet = (p) => { setPetState(p); safeSet('ui_pet', p); };

  // ── Custom theme management ────────────────────────────────────────────────
  const setThemeDraft = (spec) => setThemeDraftState(spec);          // null = stop preview
  const persistThemes = (next) => { setCustomThemes(next); safeSet('ui_custom_themes', JSON.stringify(next)); };
  // Save the draft (or any spec) as a NEW custom theme, make it active.
  const saveCustomTheme = (name, spec) => {
    const id = 'custom_' + Date.now().toString(36);
    const entry = { ...spec, name: name || 'My theme', custom: true };
    const next = { ...customThemes, [id]: entry };
    persistThemes(next);
    setThemeDraftState(null);
    setThemeState(id); safeSet('ui_theme', id);
    return id;
  };
  const setBgFx = (id) => {
    setBgFxState(id); safeSet('ui_bg_fx', id);
    try {
      const tk = localStorage.getItem('auth_token');
      if (tk) fetch('/api/users/me', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ uiPrefs: { bgFx: id } }) }).catch(() => {});
    } catch {}
  };
  // Overwrite an existing custom theme in place.
  const updateCustomTheme = (id, spec) => {
    if (!customThemes[id]) return;
    const entry = { ...customThemes[id], ...spec, custom: true };
    persistThemes({ ...customThemes, [id]: entry });
    setThemeDraftState(null);
  };
  const deleteCustomTheme = (id) => {
    if (!customThemes[id]) return;
    const next = { ...customThemes }; delete next[id];
    persistThemes(next);
    if (theme === id) { setThemeState(DEFAULT_THEME); safeSet('ui_theme', DEFAULT_THEME); }
  };

  const allThemes = { ...THEMES, ...customThemes };

  // Apply a prefs blob coming FROM the server (initial sync). Sets state +
  // localStorage cache directly; ThemeSync prevents this from echoing back.
  const hydratePrefs = (p) => {
    if (!p || typeof p !== 'object') return;
    if (typeof p.theme === 'string') { setThemeState(p.theme); safeSet('ui_theme', p.theme); }
    if (typeof p.pet === 'string') { setPetState(p.pet); safeSet('ui_pet', p.pet); }
    if (p.customThemes && typeof p.customThemes === 'object') { setCustomThemes(p.customThemes); safeSet('ui_custom_themes', JSON.stringify(p.customThemes)); }
    if (typeof p.font === 'string') { setFontState(p.font); safeSet('ui_font', p.font); }
    if (typeof p.fontScale === 'number') { setScaleState(p.fontScale); safeSet('ui_font_scale', String(p.fontScale)); }
    if (typeof p.customColor === 'string') { setCustomColor(p.customColor); safeSet('ui_custom_color', p.customColor); }
    if (typeof p.customText === 'string') { setCustomText(p.customText); safeSet('ui_custom_text', p.customText); }
    if (typeof p.customCard === 'string') { setCustomCard(p.customCard); safeSet('ui_custom_card', p.customCard); }
    if (typeof p.bgFx === 'string') { setBgFxState(p.bgFx); safeSet('ui_bg_fx', p.bgFx); }
  };

  // Apply the active theme (or live draft) as a full set of CSS variables (--app-*),
  // plus the theme's optional custom CSS, plus manual colour/font overrides.
  useEffect(() => {
    const root = document.documentElement;
    const activeSpec = themeDraft
      ? themeDraft
      : (theme === 'custom'
        ? { bg: customColor, bgSolid: customColor }
        : (allThemes[theme] || THEMES[DEFAULT_THEME]));
    const tokens = buildTokens(activeSpec);

    // Manual overrides apply only when NOT live-previewing a draft (so the editor
    // preview reflects exactly the draft spec).
    const isHex = (c) => /^#([a-f\d]{3}|[a-f\d]{6})$/i.test(c || '');
    if (!themeDraft && isHex(customText)) tokens['--app-text'] = customText;
    if (!themeDraft && isHex(customCard)) {
      tokens['--app-card'] = customCard;
      tokens['--app-card-grad'] = `linear-gradient(160deg, ${customCard}, ${tokens['--app-bg-solid']})`;
    }
    // Font override only if a draft isn't dictating a head/body font set explicitly.
    if (!themeDraft || !themeDraft.font) tokens['--app-font'] = (FONTS[font] || FONTS.manrope).stack;

    for (const [k, v] of Object.entries(tokens)) root.style.setProperty(k, v);

    root.style.background = tokens['--app-bg'];
    root.style.backgroundAttachment = 'fixed';
    root.style.backgroundSize = 'cover';
    root.style.minHeight = '100vh';
    document.body.style.background = 'transparent';
    document.body.style.color = tokens['--app-text'];
    document.body.style.transition = 'color 0.3s ease';
    document.body.style.fontFamily = tokens['--app-font'];

    // Per-theme custom CSS (keyframes / bg effects). Replaced on every switch.
    let styleEl = document.getElementById('theme-css');
    if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'theme-css'; document.head.appendChild(styleEl); }
    // Слой фона поверх темы: пользовательский выбор bgFx перекрывает фон-эффект темы.
    let css = (activeSpec && activeSpec.css) ? activeSpec.css : '';
    if (bgFx && bgFx !== 'theme') {
      const fxCss = (BG_FX[bgFx] && BG_FX[bgFx].css) || '';
      // Убираем фон-анимации самой темы (они в html/html::before/after), оставляем
      // не-фоновые правила (шрифты, скроллбары), затем добавляем выбранный слой.
      css = css.replace(/html(::(before|after))?\s*\{[^}]*\}/g, '').replace(/@keyframes\s+t[A-Za-z]+\s*\{[\s\S]*?\}\s*\}/g, '');
      css = css + fxCss;
    }
    if (styleEl.textContent !== css) styleEl.textContent = css;

    document.body.style.zoom = '';
    root.style.zoom = '';
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.style.zoom = '';

    const dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
    root.setAttribute('dir', dir);
    root.setAttribute('lang', lang);
  }, [theme, customColor, customText, customCard, font, fontScale, lang, customThemes, themeDraft, bgFx]);

  // Non-Russian locales are code-split (i18n.locales.js). Load on demand; while
  // loading, t() falls back to Russian so the UI never shows raw keys.
  // Русский словарь вшит в бандл, остальные подгружаются отдельным файлом.
  // Пока он не пришёл, t() отдавал бы русские строки — англоязычный посетитель
  // видел бы вспышку кириллицы. Поэтому держим флаг готовности.
  const [extraLocales, setExtraLocales] = useState(null);
  const localesReady = lang === 'ru' || !!extraLocales;
  useEffect(() => {
    if (lang === 'ru' || extraLocales) return;
    let alive = true;
    import('../i18n.locales.js')
      .then((m) => { if (alive) setExtraLocales(m.locales || {}); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Translation helper
  // t(key)            → перевод | ru-фолбэк | сам ключ
  // t(key, fallback)  → перевод | ru-фолбэк | fallback (для строк, которых нет в ru-словаре)
  // Режим метрик: human — словами (для обычных людей), pro — цифры и термины.
  const [metricsMode, setMetricsModeState] = useState(() => {
    try { return localStorage.getItem('metrics_mode') || 'human'; } catch { return 'human'; }
  });
  const setMetricsMode = (m) => {
    setMetricsModeState(m);
    try { localStorage.setItem('metrics_mode', m); } catch {}
  };

  const t = (key, fb) => {
    const dict = (lang === 'ru' ? translations.ru : (extraLocales?.[lang])) || translations.ru;
    return dict[key] || translations.ru[key] || (fb !== undefined ? fb : key);
  };

  return (
    <SettingsContext.Provider value={{
      metricsMode, setMetricsMode, localesReady,
      theme, setTheme, customColor, setCustom,
      customText, setCustomTextColor, customCard, setCustomCardColor,
      bgFx, setBgFx,
      font, setFont, fontScale, setFontScale,
      lang, setLang, t,
      showCat, setShowCat,
      pet, setPet,
      // theme studio
      customThemes, allThemes, themeDraft, setThemeDraft,
      saveCustomTheme, updateCustomTheme, deleteCustomTheme,
      hydratePrefs,
      isRTL: RTL_LANGS.includes(lang),
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
